import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ConfirmationModal } from 'Components/Modal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { CreateServiceRequest, FinalizationState, ScheduleV2, ThirdPartyResponseWithServices } from 'Models/TPRM';

import { IrqTargetCompletionDateSelection, getDefaultIrqTargetCompletionDate, getIrqTargetCompletionDateSelectionProps } from '../IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';

interface BaseFinalizeThirdPartyServiceModalProps {
    hideModal: () => void;
    onRequestCompleted: () => Promise<void>;
    serviceName: string;
    thirdParty: ThirdPartyResponseWithServices;
    tprmApi: ClientTPRMApi;

    irqTargetCompletionSetting?: ScheduleV2;
}

export interface FinalizeNewThirdPartyServiceModalProps extends BaseFinalizeThirdPartyServiceModalProps {
    action: 'finalizeNewService';
    getCreateRequest: (irqTargetCompletionDate?: Date) => CreateServiceRequest;
}

export interface FinalizeExistingThirdPartyServiceModalProps extends BaseFinalizeThirdPartyServiceModalProps {
    action: 'finalizeExistingService';
    serviceId: string;

    currentAssessmentWorkflowIrqTargetCompletionDate?: string;
}

/**
 * A modal that informs the user of the ramifications of finalizing a third-party service, and allows them to confirm or cancel the action.
 * Used on the Third Parties and Services Listing, for finalizing an existing service, and on the Create Third-Party Service page, for creating and finalizing a new service.
 * TODO [TPRM - managing third parties and services]: Test this component's logic comprehensively.
 */
export const FinalizeThirdPartyServiceModal = (props: FinalizeNewThirdPartyServiceModalProps | FinalizeExistingThirdPartyServiceModalProps) => {
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date | undefined>(
        getDefaultIrqTargetCompletionDate({
            action: 'startServiceAssessmentWorkflow',
            currentAssessmentWorkflowIrqTargetCompletionDate: props.action === 'finalizeExistingService' ? props.currentAssessmentWorkflowIrqTargetCompletionDate : undefined,
            irqTargetCompletionSetting: props.irqTargetCompletionSetting,
        })
    );

    return (
        <ConfirmationModal
            operationType="nonDestructive"
            headerText="Finalize Third-Party Service"
            buttonText="Finalize"
            buttonLoadingText="Finalizing..."
            buttonIcon={faCheck}
            areYouSureText={`Are you sure you want to finalize ${props.serviceName}?`}
            performOperation={async () => {
                const irq_target_completion_date = selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined;

                switch (props.action) {
                    case 'finalizeNewService':
                        await props.tprmApi.createThirdPartyService(props.getCreateRequest(selectedIrqTargetCompletionDate), props.thirdParty.id);
                        await props.onRequestCompleted();
                        return 'Third-party service created.';
                    case 'finalizeExistingService':
                        await props.tprmApi.finalizeThirdPartyService(props.thirdParty.id, props.serviceId, { irq_target_completion_date });
                        await props.onRequestCompleted();
                        return 'Third-party service finalized.';
                }
            }}
            hideModal={props.hideModal}
        >
            {props.action === 'finalizeExistingService' && <Text>This third-party service will no longer be a draft; users who do not have full access to Third-Party Risk Management will no longer be able to edit it. The assessment workflow will be started.</Text>}
            {props.action === 'finalizeNewService' && <Text>The assessment workflow will be started.</Text>}
            {props.thirdParty.finalization_state !== FinalizationState.FINALIZED && <Text>Finalizing this service will also finalize {props.thirdParty.name}.</Text>}
            <IrqTargetCompletionDateSelection
                {...getIrqTargetCompletionDateSelectionProps(
                    {
                        action: 'startServiceAssessmentWorkflow',
                        currentAssessmentWorkflowIrqTargetCompletionDate: props.action === 'finalizeExistingService' ? props.currentAssessmentWorkflowIrqTargetCompletionDate : undefined,
                        irqTargetCompletionSetting: props.irqTargetCompletionSetting,
                    },
                    setSelectedIrqTargetCompletionDate
                )}
            />
        </ConfirmationModal>
    );
};
